import { lazy, Suspense } from "react";

import { Session } from "util/Session";

import { IconButton, styled } from "@mui/material";
import { Icon, IconNameType } from "components/atoms/icons";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Progress } from "../components/atoms/Progress";
import { RouteAuthGuard } from "../provider/routerAuthGuard";

const LoginPage = lazy(() => import("./LoginPage"));
const InitialSettingPage = lazy(() => import("./InitialSettingPage"));
const ConsultationListPage = lazy(() => import("./ConsultationListPage"));
const CouponListPage = lazy(() => import("./CouponListPage"));
const CouponDetailPage = lazy(() => import("./CouponDetailPage"));
const ClinicAccountsPage = lazy(() => import("./ClinicAccountsPage"));
const ClinicAccountDetailPage = lazy(() => import("./ClinicAccountDetailPage"));
const SettingsPage = lazy(() => import("./SettingsPage"));
const ClinicPage = lazy(() => import("./ClinicPage"));
const PatientsListPage = lazy(() => import("./PatientListPage"));
const PaymentItemListPage = lazy(() => import("./PaymentItemListPage"));
const PatientDetailPage = lazy(() => import("./PatientDetailPage"));
const ConsultationPage = lazy(() => import("./ConsultationPage"));
const OnlineConsultationPage = lazy(() => import("./OnlineConsultationPage"));
const NotFoundPage = lazy(() => import("./NotFoundPage"));
const ForbiddenPage = lazy(() => import("./ForbiddenPage"));
const ScheduleManagementPage = lazy(() => import("./ScheduleManagementPage"));
const VerifyClinicAccountEmailPage = lazy(
  () => import("./VerifyClinicAccountEmailPage")
);

const buildBreadCrumb = (arr: { title: string; path?: string }[]) => {
  return arr;
};
export const pages = [
  {
    path: "/",
    component: <ConsultationListPage />,
    breadCrumb: () => buildBreadCrumb([{ title: "診療", path: "/" }]),
  },
  {
    path: "/consultation/:id",
    component: <ConsultationPage />,
    breadCrumb: (name: string) =>
      buildBreadCrumb([
        { title: "診療", path: "/" },
        { title: `診療情報詳細 ${name}` },
      ]),
  },
  {
    path: "/online-consultation/:id",
    component: <OnlineConsultationPage />,
  },
  {
    path: "/login",
    component: <LoginPage />,
  },
  {
    path: "/clinicAccounts",
    component: <ClinicAccountsPage />,
    breadCrumb: () =>
      buildBreadCrumb([{ title: "アカウント", path: "/clinicAccounts" }]),
  },
  {
    path: "/clinicAccountDetail/:id",
    component: <ClinicAccountDetailPage />,
    breadCrumb: (name: string) =>
      buildBreadCrumb([
        { title: "アカウント", path: "/clinicAccounts" },
        { title: `${name}` },
      ]),
  },
  {
    path: "/patients",
    component: <PatientsListPage />,
    breadCrumb: () => buildBreadCrumb([{ title: "患者", path: "/patients" }]),
  },
  {
    path: "/paymentItemList",
    component: <PaymentItemListPage />,
    breadCrumb: () =>
      buildBreadCrumb([
        { title: "設定" },
        { title: "精算項目", path: "/paymentItemList" },
      ]),
  },
  {
    path: "/patients/:id",
    component: <PatientDetailPage />,
    breadCrumb: (name: string) =>
      buildBreadCrumb([
        { title: "患者", path: "/patients" },
        { title: `${name}` },
      ]),
  },
  {
    path: "/settings",
    component: <SettingsPage />,
    breadCrumb: () =>
      buildBreadCrumb([{ title: "マイページ", path: "/settings" }]),
  },
  {
    path: "/clinic",
    component: <ClinicPage />,
    breadCrumb: () =>
      buildBreadCrumb([
        { title: "設定" },
        { title: "病院詳細", path: "/clinic" },
      ]),
  },
  {
    path: "/initial-setting",
    component: <InitialSettingPage />,
  },
  {
    path: "/scheduleManagement",
    component: <ScheduleManagementPage />,
    breadCrumb: () =>
      buildBreadCrumb([
        { title: "医師スケジュール", path: "/scheduleManagement" },
      ]),
  },
  {
    path: "/coupon",
    component: <CouponListPage />,
    breadCrumb: () => buildBreadCrumb([{ title: "クーポン", path: "/coupon" }]),
  },
  {
    path: "/coupon/:id",
    component: <CouponDetailPage />,
    breadCrumb: (name: string) =>
      buildBreadCrumb([
        { title: "クーポン", path: "/coupon" },
        { title: `${name}` },
      ]),
  },
];

const StyledContainer = styled(ToastContainer)`
  .Toastify__toast--info {
    color: #009957;
    border-radius: 4px;
    border: 1px solid #9ce0c3;
    background: #e5f3ed;
  }

  .Toastify__toast--success {
    color: #009957;
    border-radius: 4px;
    border: 1px solid #9ce0c3;
    background: #e5f3ed;
  }

  .Toastify__toast--error {
    color: #e31717;
    borderradius: 4px;
    border: 1px solid #e31717;
    background: #fff3f3;
  }

  .Toastify__close-button {
    alignself: none;
  }
`;

type ToastType = {
  [key: string]: {
    color: string;
    iconName?: IconNameType;
  };
};

const toastColor: ToastType = {
  success: {
    color: "#009957",
    iconName: "checkOutline",
  },
  info: {
    color: "#009957",
    iconName: "infoOutline",
  },
  error: {
    color: "#E31717",
    iconName: "error",
  },
  // Figmaでデザインに起こしてもらう
  warning: {
    color: "#E31717",
  },
  default: {
    color: "#fff",
  },
};

/*
MEMO:
  Suspenseコンポーネントの外側にSessionコンポーネントをマウントし、
  遅延読み込み中でも実行されるようにする
*/
export const Router = () => {
  return (
    <BrowserRouter>
      <Session />
      <Suspense fallback={<Progress />}>
        <StyledContainer
          style={{ minWidth: "500px" }}
          position="top-center"
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          icon={({ type }) =>
            toastColor[type].iconName && (
              <Icon
                iconName={toastColor[type].iconName as IconNameType}
                size={24}
                color={toastColor[type].color}
              />
            )
          }
          closeButton={({ type }) => (
            <IconButton>
              <Icon iconName="close" size={20} color={toastColor[type].color} />
            </IconButton>
          )}
        />
        <Routes>
          <>
            {pages.map((item, index) => (
              <Route
                key={index}
                path={item.path}
                element={<RouteAuthGuard component={item.component} />}
              />
            ))}
            {/*
              確認メール内の URL からアクセスされるため認証ガードなし。
              未ログイン状態でも表示できる必要がある。
            */}
            <Route
              path="/verifyClinicAccountEmail"
              element={<VerifyClinicAccountEmailPage />}
            />
            <Route
              path="/forbidden"
              element={<RouteAuthGuard component={<ForbiddenPage />} />}
            />
            <Route
              path="*"
              element={<RouteAuthGuard component={<NotFoundPage />} />}
            />
          </>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
